/**
 * Shared "go to Home" primitive used by both the BackButtonController and the
 * BottomNav. The goal is to keep Home as the history ROOT so the Android
 * double-back-to-exit can rely on the OS closing the PWA at index 0.
 *
 * If the Navigation API is available (Android Chrome always, iOS 26.2+) and the
 * history already contains an earlier `/home` entry, we TRAVERSE back to it —
 * any pages that were "forward" of Home (e.g. the booking funnel) are discarded
 * on the next push, leaving Home at the root. Otherwise we synthesize Home with
 * `router.replace` (the deep-link / shortcut case where no Home exists beneath).
 *
 * The traversal fires a `popstate`/`navigate`; we flag it via `suppressNextPop`
 * so the BackButtonController ignores it (this is a deliberate go-home, not a
 * user back press — otherwise it would misfire the exit toast).
 */

import { setSuppressNextPop } from "@/lib/overlay-stack"

export const HOME = "/home"

interface NavEntryLite {
  url: string | null
  key: string
}

interface NavigationLite {
  entries(): NavEntryLite[]
  currentEntry: { index: number } | null
  traverseTo(key: string): unknown
}

type ReplaceRouter = { replace: (href: string) => void }

export function goHome(router: ReplaceRouter): void {
  if (typeof window === "undefined") return

  const nav = (window as unknown as { navigation?: NavigationLite }).navigation
  if (nav && typeof nav.traverseTo === "function") {
    try {
      const entries = nav.entries()
      const current = nav.currentEntry?.index ?? entries.length - 1
      // Earliest `/home` entry = the true root (sentinels sit above the original).
      for (let i = 0; i < current; i++) {
        const url = entries[i]?.url
        if (url && safePathname(url) === HOME) {
          // The controller must treat this traversal as a deliberate go-home,
          // not a user back press.
          setSuppressNextPop(true)
          nav.traverseTo(entries[i].key)
          return
        }
      }
    } catch {
      setSuppressNextPop(false)
      // Fall through to router.replace below.
    }
  }

  router.replace(HOME)
}

function safePathname(url: string): string | null {
  try {
    return new URL(url).pathname
  } catch {
    return null
  }
}
